// Quill 2 positions its link tooltip against one element, `boundsContainer`,
// which defaults to document.body, and PrimeReact's Editor builds the Quill
// config itself so Quill's own `bounds` option cannot be passed as a prop.
// On a wide screen the editor card is far narrower than the body, so the body
// clamp never fires: a selection at the end of a line puts the tooltip past
// the card, where an ancestor overflow:hidden cuts off the Save control and
// the link cannot be saved at all. Reported by an editor on a German draft,
// 30 Oct 2026, and the same for Hebrew where the overflow goes the other way.
//
// Quill reads boundsContainer in exactly one place, position(), and only calls
// getBoundingClientRect() on it, so a duck-typed object is enough. The rect is
// the editor box intersected with the viewport: the editor box keeps the
// tooltip inside the clipping ancestor horizontally, the viewport makes Quill
// flip the tooltip above the selection near the bottom of the screen instead
// of leaving it below the fold.
const EDGE_PADDING = 8;

export function confineQuillTooltip(quill) {
  const tooltip = quill && quill.theme && quill.theme.tooltip;
  const container = quill && quill.container;
  if (!tooltip || !container) return;

  tooltip.boundsContainer = {
    getBoundingClientRect() {
      const box = container.getBoundingClientRect();
      const viewWidth = window.innerWidth || box.right;
      const viewHeight = window.innerHeight || box.bottom;
      const left = Math.max(box.left + EDGE_PADDING, EDGE_PADDING);
      const right = Math.min(box.right - EDGE_PADDING, viewWidth - EDGE_PADDING);
      const top = Math.max(box.top, 0);
      // A flip moves the tooltip just above the selection, so claiming the
      // editor's own bottom is only safe where a flipped tooltip still lands
      // inside the editor. The single-line title and subheader editors are
      // shorter than that, and there the viewport is the only bound.
      const tooltipHeight = (tooltip.root && tooltip.root.offsetHeight) || 0;
      const canFlipInside = box.height > tooltipHeight * 2 + EDGE_PADDING;
      const bottom = Math.min(canFlipInside ? box.bottom : viewHeight, viewHeight);
      return {
        left,
        right,
        top,
        bottom,
        x: left,
        y: top,
        width: Math.max(0, right - left),
        height: Math.max(0, bottom - top),
      };
    },
  };
}

export default confineQuillTooltip;
